import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { login } from "@/utils/auth";
import { toast } from "sonner";

/**
 * Handles login + 2FA + force-password-change flows uniformly across role login pages.
 *
 * Usage:
 *   const { handleLogin, flowState, loading } = useLoginFlow("admin", "/admin/dashboard");
 *   ...
 *   <form onSubmit={handleLogin(username, password)}>...</form>
 *   <LoginFlowStepper state={flowState} />   // renders 2FA / force-pwd dialogs
 */
export const useLoginFlow = (role, redirectTo) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  // State carrying 2FA and force-password-change info between backend responses + dialogs
  const [flow, setFlow] = useState({
    twoFactor: null, // { open, emailHint, devOtp }
    forcePwd: null,  // { open, currentPassword }
  });

  const completeLogin = () => {
    navigate(redirectTo);
  };

  const handleLogin = async (username, password) => {
    setLoading(true);
    try {
      const data = await login(username, password, role);
      if (data.requires_2fa) {
        setFlow({
          twoFactor: {
            open: true,
            emailHint: data.email_hint,
            devOtp: data.dev_otp,
            // Save password so that after OTP verification, if force_password_change is flagged,
            // we can open the password-change dialog using the same current password.
            currentPassword: password,
          },
          forcePwd: null,
        });
        return;
      }
      if (data.force_password_change) {
        setFlow({
          twoFactor: null,
          forcePwd: { open: true, currentPassword: password },
        });
        return;
      }
      toast.success("Login successful!");
      completeLogin();
    } catch {
      toast.error("Incorrect username or password");
    } finally {
      setLoading(false);
    }
  };

  // After 2FA verify succeeds, this handler continues the flow.
  const handleOtpVerified = (data) => {
    if (data.force_password_change) {
      setFlow((f) => ({
        twoFactor: null,
        forcePwd: { open: true, currentPassword: f.twoFactor?.currentPassword || "" },
      }));
      return;
    }
    toast.success("Login successful!");
    completeLogin();
  };

  const handlePasswordChanged = () => {
    completeLogin();
  };

  return { handleLogin, loading, flow, setFlow, role, handleOtpVerified, handlePasswordChanged };
};
